import { useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { AtsJobApplicationFilesOfferLetterDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { SendOfferLetterModal } from 'modules/ats/components/SendOfferLetterModal'
import { OfferLetterFromScratchModal } from 'modules/ats/components/SendOfferLetterModal/OfferLetterFromScratch'
import { OfferLetterFromTemplateModal } from 'modules/ats/components/SendOfferLetterModal/OfferLetterFromTemplate'
import { SenfOfferLetterFromTemplateModal } from 'modules/ats/components/SendOfferLetterModal/SenfOfferLetterFromTemplate'

import { OfferLetterItem } from './OfferLetterItem'

interface Props {
  applicationId: number
  jobPostingId: number
}

export const OfferLetter = ({ applicationId, jobPostingId }: Props) => {
  const { company } = useCurrentSession()
  const [openedModal, setOpenedModal] = useState<
    | 'send-offer-letter'
    | 'create-offer-letter-from-template'
    | 'send-offer-letter-from-scratch'
    | 'send-offer-letter-from-template'
    | undefined
  >(undefined)
  const { data } = useQuery(AtsJobApplicationFilesOfferLetterDocument, {
    variables: { applicationId, companyId: company.id, jobPostingId },
  })

  const devOfferLetterTemplatesEnabled =
    data?.features.enabledFeatures.nodes.find(
      (node) => node.name === 'dev_ats_offer_letter_templates'
    )?.enabled ?? false

  const offerLetterData = data?.ats.offerLetters[0]
  const applicationData = data?.ats.application

  if (!applicationData) {
    return null
  }

  return (
    <>
      {offerLetterData ? (
        <Box gap="s24">
          <Text.Normal color="textPrimary" weight="semibold">
            {i18n.t('ats.candidates.files.offer_letter.header')}
          </Text.Normal>
          <OfferLetterItem
            offerLetter={offerLetterData}
            applicationId={applicationId}
            jobPostingId={jobPostingId}
          />
        </Box>
      ) : (
        <Box
          flexDirection="row"
          gap="s12"
          paddingX="s16"
          paddingY="s12"
          justifyContent="spaceBetween"
          width="full"
          alignItems="center"
          borderRadius={{ all: 'abs016' }}
          border={{
            all: {
              width: 's1',
              style: 'solid',
              color: 'grey400',
            },
          }}
        >
          <Box gap="s2">
            <Text.Normal size="200" color="textPrimary" weight="medium">
              {i18n.t('ats.candidates.files.offer_letter.header')}
            </Text.Normal>
            <Text.Normal size="200">
              {i18n.t('ats.candidates.files.offer_letter.description')}
            </Text.Normal>
          </Box>
          <F0Button
            label={i18n.t('ats.offer_letter.send_offer_letter')}
            onClick={() => setOpenedModal('send-offer-letter')}
          />
        </Box>
      )}

      {openedModal === 'send-offer-letter' &&
        // TO DO: show only SendOfferLetterModal when feature is ready
        (devOfferLetterTemplatesEnabled ? (
          <SendOfferLetterModal
            onSelectOption={(option) => {
              setOpenedModal(option)
            }}
            onClose={() => setOpenedModal(undefined)}
          />
        ) : (
          <OfferLetterFromScratchModal
            application={applicationData}
            onClose={() => setOpenedModal(undefined)}
          />
        ))}

      {openedModal === 'send-offer-letter-from-scratch' && (
        <OfferLetterFromScratchModal
          application={applicationData}
          onClose={() => setOpenedModal(undefined)}
        />
      )}

      {openedModal === 'create-offer-letter-from-template' && (
        <OfferLetterFromTemplateModal
          jobApplicationId={applicationId}
          onClose={() => setOpenedModal(undefined)}
          onSuccess={() => setOpenedModal('send-offer-letter-from-template')}
        />
      )}

      {openedModal === 'send-offer-letter-from-template' && (
        <SenfOfferLetterFromTemplateModal
          applicationData={applicationData}
          onClose={() => setOpenedModal(undefined)}
        />
      )}
    </>
  )
}
